// react
import { useEffect, useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Button, Divider } from '@mui/material';
import debounce from '@mui/utils/debounce';
import { DateTimePicker } from '@mui/x-date-pickers';

import type { DateTime } from 'luxon';

interface DateRangeSelectorProps {
  from: DateTime | null;
  to: DateTime | null;
  onFromChange: (dt: DateTime | null) => void;
  onToChange: (dt: DateTime | null) => void;
  onToday: () => void;
  liveMode: boolean;
  onToggleLive: () => void;
  containerDataTour?: string;
  todayButtonDataTour?: string;
  liveButtonDataTour?: string;
}

const DEBOUNCE_MS = 500;

const DateRangeSelector = ({
  from,
  to,
  onFromChange,
  onToChange,
  onToday,
  liveMode,
  onToggleLive,
  containerDataTour,
  todayButtonDataTour,
  liveButtonDataTour,
}: DateRangeSelectorProps) => {
  const { t } = useTranslation('view-data');
  const debouncedFromChange = useMemo(
    () => debounce(onFromChange, DEBOUNCE_MS),
    [onFromChange],
  );
  const debouncedToChange = useMemo(
    () => debounce(onToChange, DEBOUNCE_MS),
    [onToChange],
  );

  useEffect(() => {
    return () => {
      debouncedFromChange.clear();
      debouncedToChange.clear();
    };
  }, [debouncedFromChange, debouncedToChange]);

  const handleFromChange = (dt: DateTime | null) => {
    debouncedFromChange(dt);
  };

  const handleToChange = (dt: DateTime | null) => {
    debouncedToChange(dt);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'flex-end',
        justifyContent: liveMode ? 'flex-end' : 'flex-start',
      }}
      minWidth={{ xs: '100%', md: 'auto' }}
      data-tour={containerDataTour}
    >
      {!liveMode && (
        <>
          <DateTimePicker
            label={t('from')}
            value={from}
            onChange={handleFromChange}
            slotProps={{
              textField: {
                size: 'small',
                variant: 'outlined',
                sx: {
                  minWidth: { xs: '100%', md: 176 },
                },
              },
            }}
          />
          <DateTimePicker
            label={t('to')}
            value={to}
            onChange={handleToChange}
            slotProps={{
              textField: {
                size: 'small',
                variant: 'outlined',
                sx: {
                  minWidth: { xs: '100%', md: 176 },
                },
              },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              height: 40,
              minHeight: 40,
              alignSelf: 'flex-end',
              flexShrink: 0,
            }}
            onClick={onToday}
            data-tour={todayButtonDataTour}
          >
            {t('today')}
          </Button>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <Button
        variant={liveMode ? 'contained' : 'outlined'}
        color={liveMode ? 'primary' : 'inherit'}
        size="small"
        onClick={onToggleLive}
        data-tour={liveButtonDataTour}
        startIcon={
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: liveMode ? 'success.main' : 'grey.500',
              boxShadow: liveMode
                ? '0 0 0 3px rgba(46, 125, 50, 0.18)'
                : 'none',
            }}
          />
        }
        sx={{
          whiteSpace: 'nowrap',
          height: 40,
          minHeight: 40,
          alignSelf: 'flex-end',
          flexShrink: 0,
        }}
      >
        {t('live')}
      </Button>
    </Box>
  );
};

export default DateRangeSelector;
